import { z } from 'zod';
import { macroAuthoringSourceGraphSchema } from '../macroPackage/sourceGraph';
import { sourceGraphIdentitySchema } from '../macroPackage/workflow';
import { compiledPackageRefSchema, macroContentViewportSchema, macroPackageRunReplySchema } from '../macroPackage/packageRun';
import { macroTriggerSchema } from '../macroTree';
import { completionRootSchema, chunkDescriptorSchema } from '../macroCalibration/protocol';
import { readGrantSchema } from '../macroCalibration/jobs';
import { macroVisualDigestSchema } from '../macroCalibration/common';
import { hostTargetSchema } from '../macroPackage/pipelineContracts';
import { visualTestPreviewSettingsSchema, visualTestSensitivitySchema } from '../macroExecution';

export const LOCAL_SOURCE_GRAPH_PATH = '/macro-editor/source-graphs';
export const LOCAL_COMPILED_PACKAGE_PATH = '/macro-editor/compiled-packages';
export const localSourceGraphPrepareSchema = z.object({ source: macroAuthoringSourceGraphSchema,
  rev: z.number().int().positive().safe() }).strict();
export const localSourceGraphCompileSchema = localSourceGraphPrepareSchema.extend({ selectedHost: hostTargetSchema });
export const localSourceGraphPreparedSchema = z.object({ analysisId: z.string().uuid(),
  sourceIdentity: sourceGraphIdentitySchema, manifest: readGrantSchema,
  manifestDigest: macroVisualDigestSchema, workPlanDigest: macroVisualDigestSchema,
  expectedBindings: z.number().int().nonnegative().max(16384),
  expectedComparisons: z.number().int().nonnegative().max(1048576),
  assetGrantEndpoint: z.string().url().max(2048), assetToken: z.string().min(1).max(4096),
  expiresAtUnixMs: z.number().int().positive() }).strict();
export const localSourceGraphCalibrationReuseSchema = z.object({ reused: z.literal(true),
  artifactDigest: macroVisualDigestSchema, sourceIdentity: sourceGraphIdentitySchema }).strict();
export const localSourceGraphPrepareReplySchema = z.union([
  localSourceGraphPreparedSchema, localSourceGraphCalibrationReuseSchema,
]);
export const localSourceGraphFinishSchema = z.object({ analysisId: z.string().uuid(),
  completion: completionRootSchema, chunks: z.array(chunkDescriptorSchema).max(8192) }).strict();
export const localSourceGraphTargetSchema = z.object({ campaignId: z.string().min(1).max(128),
  trigger: macroTriggerSchema, rev: z.number().int().positive().safe() }).strict();
export const localCompiledPackageUploadSchema = z.object({ packageSha256: macroVisualDigestSchema,
  files: z.array(z.object({ file: z.string().regex(/^(compiled\.json|refs\/page-\d{1,3}\.bin|evidence\/index\.json|evidence\/images\/[0-9a-f]{64}\.png|evidence\/descriptors\/[0-9a-f]{64}\.bin|evidence\/(reports|pages)\/[0-9a-f]{64}\.json)$/),
    base64: z.string().min(1).max(36 * 1024 * 1024).regex(/^[A-Za-z0-9+/]*={0,2}$/) }).strict()).min(1).max(65_536),
}).strict().refine(value => new Set(value.files.map(file => file.file)).size === value.files.length
  && value.files.some(file => file.file === 'compiled.json'), 'Compiled upload requires unique files and its manifest');
export const localCompiledPackageStoredSchema = z.object({ packageSha256: macroVisualDigestSchema,
  package: compiledPackageRefSchema }).strict();
export const localCompiledPackageCommandSchema = z.discriminatedUnion('command', [
  z.object({ command: z.literal('capabilities') }).strict(),
  z.object({ command: z.literal('validate'), packageSha256: macroVisualDigestSchema, package: compiledPackageRefSchema,
    expectedHost: hostTargetSchema, validationContextDigest: macroVisualDigestSchema }).strict(),
  z.object({ command: z.literal('start'), packageSha256: macroVisualDigestSchema, package: compiledPackageRefSchema,
    mode: z.enum(['local-calibrated', 'local-shadow']).optional(),
    liveDetection: z.enum(['continuous', 'sequence']).optional(), previewSettings: visualTestPreviewSettingsSchema.optional(),
    contentViewport: macroContentViewportSchema.optional() }).strict(),
  z.object({ command: z.literal('preview-sensitivity'), runId: z.string().uuid(), sensitivity: visualTestSensitivitySchema,
    rawScores: z.boolean() }).strict(),
  z.object({ command: z.literal('status'), runId: z.string().uuid() }).strict(),
  z.object({ command: z.literal('cancel'), runId: z.string().uuid() }).strict(),
]).superRefine((request, context) => {
  if (request.command === 'start' && request.liveDetection && request.mode !== 'local-shadow') {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Live Detection requires an observation run' });
  }
  if (request.command === 'start' && request.previewSettings && !request.liveDetection) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Preview settings require Live Detection' });
  }
  if (request.command === 'start' && request.contentViewport && (!request.liveDetection || request.mode !== 'local-shadow')) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Content viewport requires Live Detection observation', path: ['contentViewport'] });
  }
});
export const localCompiledPackageReplySchema = z.object({ requestId: z.string().uuid(), success: z.boolean(),
  reply: macroPackageRunReplySchema.optional(), error: z.string().max(256).optional() }).strict();
export type LocalSourceGraphPrepared = z.infer<typeof localSourceGraphPreparedSchema>;
export type LocalSourceGraphPrepareReply = z.infer<typeof localSourceGraphPrepareReplySchema>;
export type LocalSourceGraphFinish = z.infer<typeof localSourceGraphFinishSchema>;
